import { Fragment, type CSSProperties, type ReactNode } from 'react';
import { monogramInitials } from '@icebreaker/shared/design';
import { webAvatarSize as WEB_AVATAR_SIZE } from '@icebreaker/shared/design/web-tema';
import styles from './avatar.module.css';

export type AvatarSize = keyof typeof WEB_AVATAR_SIZE;
/** Şekil anlam taşır: `person` daire, `entity` (etkinlik/şirket) köşeli. */
export type AvatarShape = 'person' | 'entity';

/**
 * Basamağa göre monogram punto — Avatar kartı (`wb-avatar--24` … `--96`):
 * 24 → 10, 32 → 13, 40 → 15, 56 → 20, 96 → 34.
 */
const MONOGRAM_FONT_SIZE: Readonly<Record<(typeof WEB_AVATAR_SIZE)[AvatarSize], number>> = {
  24: 10,
  32: 13,
  40: 15,
  56: 20,
  96: 34,
};

export interface AvatarProps {
  /** Monogram ve sabit ton bu isimden türetilir. */
  readonly name: string;
  readonly photoUrl?: string | null;
  readonly size?: AvatarSize;
  readonly shape?: AvatarShape;
  /**
   * Fotoğrafın alternatif metni. Avatar isimle birlikte bir satırda okunuyorsa
   * bilgi tekrarlanmasın diye boş bırakılır (dekoratif).
   */
  readonly alt?: string;
  readonly className?: string;
  /**
   * SABİT ZEMİN (yalnız kurum işareti verir, `OrgMark`): verilirse addan türeyen
   * sıcak ton yerine bu zemin ve yazı rengi İKİ temada da kullanılır. Değer
   * tema değişkeni de olabilir (`var(--ib-…)`), o zaman tema ile döner.
   */
  readonly tone?: AvatarTone;
  /**
   * MONOGRAMIN ERİŞİLEBİLİR ADI (yalnız kurum işareti verir, `OrgMark.alt`).
   * Verilmezse monogram dekoratiftir (`aria-hidden`, kişi satırlarının önceki
   * hâli); verilirse ekran okuyucu baş harfleri harf harf değil bu adı okur.
   */
  readonly label?: string;
  /**
   * Zemin halkası (`wb-avatar--ring`): bağ çizgisinin ucundaki avatar zeminden
   * 3 px halkayla ayrılır (ConnectionLine kartı).
   */
  readonly ring?: boolean;
}

/** Sabit monogram zemini; `id` `data-tone` olarak basılır (test ve hata ayıklama). */
export interface AvatarTone {
  readonly id: string;
  readonly background: string;
  readonly foreground: string;
}

/**
 * Sabit zemin yalnız kurum işaretinde verilir; kişi monogramı tasarımın tek
 * tonunu (`primary-subtle` zemin, `primary-strong` yazı) modülden alır.
 */
function toneVariables(tone: AvatarTone | undefined): CSSProperties {
  if (!tone) return {};
  return {
    '--ib-avatar-bg': tone.background,
    '--ib-avatar-fg': tone.foreground,
  } as CSSProperties;
}

/**
 * Avatar (Avatar kartı, yeni görünüm). Fotoğraf varsa yuvarlak kırpılır
 * (`object-fit: cover`) ve filtre uygulanmaz; yoksa ad ve soyadın baş harfleri
 * `primary-subtle` zemin, `primary-strong` yazı ve başlık ailesi 600 ile
 * monogram olur. Ton addan TÜREMEZ: tasarımda tek monogram tonu var, aynı
 * kişi her ekranda yine aynı renktedir. Çizim avatar üründe kullanılmaz.
 */
export function Avatar({
  name,
  photoUrl,
  size = 'lg',
  shape = 'person',
  alt,
  className,
  tone,
  label,
  ring = false,
}: AvatarProps) {
  const px = WEB_AVATAR_SIZE[size];
  const style: CSSProperties = {
    ...toneVariables(tone),
    ['--ib-avatar-px' as string]: `${px}px`,
    ['--ib-avatar-fs' as string]: `${MONOGRAM_FONT_SIZE[px]}px`,
  };

  const shapeClass = shape === 'person' ? styles.person : styles.entity;

  return (
    <span
      className={[
        styles.avatar,
        shapeClass,
        tone ? styles.toned : '',
        ring ? styles.ring : '',
        className ?? '',
      ]
        .filter(Boolean)
        .join(' ')}
      style={style}
      data-tone={tone?.id}
      data-avatar-size={px}
      // Monogram dekoratiftir (isim zaten satırda yazılı); ad verildiyse görsel olarak okunur.
      aria-hidden={photoUrl || label ? undefined : 'true'}
      role={!photoUrl && label ? 'img' : undefined}
      aria-label={!photoUrl && label ? label : undefined}
    >
      {photoUrl ? (
        /* Avatar kaynakları R2'den gelir ve boyutu token'la sabitlenmiştir.
           DÜZ <img> BİLİNÇLİDİR: kit çerçeve bilmez (bkz. `link-slot.ts` ve
           `framework-freedom.guard.spec.ts`), yani `next/image` buraya giremez.
           Optimizasyon zaten gerekmiyor: ölçü token'la sabit, uzak host
           yapılandırması istemiyor. Next'in `no-img-element` kuralı kit
           yapılandırmasında TANIMLI DEĞİLDİR; bastırma yorumu da bu yüzden yok. */
        <img className={styles.photo} src={photoUrl} alt={alt ?? ''} width={px} height={px} />
      ) : (
        monogramInitials(name)
      )}
    </span>
  );
}

export interface AvatarStackPerson {
  /**
   * Kararlı kimlik (kişi/üyelik kimliği). Ad anahtar OLAMAZ: kurum hesabı
   * üyeleri kurumun adını taşır (B-18), aynı adlı iki kişi React anahtarını
   * çakıştırırdı. Verilmezse sıra + ad kullanılır.
   */
  readonly id?: string;
  readonly name: string;
  readonly photoUrl?: string | null;
  /**
   * KİŞİ DAİRESİ YERİNE ÇİZİLECEK İŞARET (yeni görünüm K2, tech-lead). Kurum
   * hesabı üyesi kişi değildir, kurumun işaretiyle (`OrgMark`, B-18) çizilir;
   * yığının bindirmesi, halkası ve "+N"i aynı kalır. Verilirse `name` ve
   * `photoUrl` çizilmez. Yığın yalnız işareti yerleştirir, kurum kuralını bilmez.
   */
  readonly mark?: ReactNode;
}

export interface AvatarStackProps {
  readonly people: readonly AvatarStackPerson[];
  /** Kaç avatar gösterilecek; kalanı "+N" olarak özetlenir. */
  readonly max?: number;
  /**
   * KALABALIĞIN GERÇEK BÜYÜKLÜĞÜ (K4 · TW2): sunucu yalnız ilk birkaç yüzü
   * döndürdüğünde "+N" listenin değil toplamın kalanıdır. Verilmezse liste
   * uzunluğu kullanılır (eski davranış).
   */
  readonly total?: number;
  readonly size?: AvatarSize;
  /** Yığının yanında okunacak cümle — "412 kişi geliyor" gibi. */
  readonly children?: ReactNode;
  /** Yığın kart yüzeyinde değil sayfa zemininde duruyorsa kenarlık rengi değişir. */
  readonly onBackground?: boolean;
  readonly className?: string;
}

/**
 * Kalabalık sayı değil, yüz olarak gösterilir (Avatar kartı, "Yığın"): avatarlar
 * 8 px (40 ve üstünde 10 px) bindirilir, 2 px zemin halkasıyla ayrılır; kalan
 * gerçek sayı (`total`) "+N" dairesinde yazılır.
 */
export function AvatarStack({
  people,
  max = 5,
  total,
  size = 'sm',
  children,
  onBackground = false,
  className,
}: AvatarStackProps) {
  const shown = people.slice(0, max);
  const remaining = Math.max(0, (total ?? people.length) - shown.length);
  // Metin JSX literali olarak değil ifade olarak üretilir (i18n kuralı: gömülü metin yok).
  const remainingLabel = `+${remaining}`;

  return (
    <div
      className={[
        styles.stack,
        WEB_AVATAR_SIZE[size] >= 40 ? styles.stackWide : '',
        onBackground ? styles.onBackground : '',
        className ?? '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <div className={styles.stackRow} aria-hidden="true">
        {shown.map((person, index) => {
          const key = person.id ?? `${index}:${person.name}`;
          // İşaret parçayla doğrudan satırın çocuğu olur: halka ve bindirme ona da uygulanır.
          return person.mark ? (
            <Fragment key={key}>{person.mark}</Fragment>
          ) : (
            <Avatar key={key} name={person.name} photoUrl={person.photoUrl ?? null} size={size} />
          );
        })}
        {remaining > 0 ? (
          <span
            className={styles.more}
            style={{ ['--ib-avatar-px' as string]: `${WEB_AVATAR_SIZE[size]}px` }}
          >
            {remainingLabel}
          </span>
        ) : null}
      </div>
      {children ? <p className={styles.stackLabel}>{children}</p> : null}
    </div>
  );
}

/** Yığın cümlesinde sayıyı vurgulamak için (tabular). */
export function AvatarStackCount({ children }: { readonly children: ReactNode }) {
  return <b className={styles.stackCount}>{children}</b>;
}
